import { AHSeparator, Box, useBoxStyle } from "arkh";
import { SectionList } from "react-native";
import { FormattedMessage, useIntl } from "react-intl";
import React, { useMemo } from "react";
import {
  IntegrationLogoItem,
  IntegrationTextItem,
} from "./components/IntegrationAddItem";

import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { graphql, useLazyLoadQuery } from "react-relay";
import { IntegrationsAdminScreenQuery } from "./__generated__/IntegrationsAdminScreenQuery.graphql";
import {
  ListSectionHeader,
  ListSectionHeaderTitle,
} from "../../components/atoms/ListSectionHeader";

import { IntegrationAdminHRProviderListItem } from "./IntegrationAdminHRProviderListItem";

import { IntegrationAdminFlyerListItem } from "./IntegrationAdminFlyerListItem";

import {
  useHasFeatureFlag,
  useIsStoresprintAdmin,
} from "../../useHasFeatureFlag";
import { FeatureFlags } from "../../config";

import { StoreAdminNavigationScreenProps } from "../StoreAdmin/StoreAdminNavigation";

import { IntegrationAdminNewsfeedListItem } from "./IntegrationNewsfeedScreen";
import { IntegrationAdminServiceAccountsListItem } from "./IntegrationAdminServiceAccountsListItem";
import { IntegrationAdminPollFileListItem } from "./IntegrationAdminPollFileListItem";
import { IntegrationAdminWebFetcherListItem } from "./IntegrationAdminWebFetcherListItem";

import { IntegrationAdminIcaMinButikListItemListItem } from "./IntegrationAdminIcaMinButikListItem";

export function IntegrationsAdminScreen(
  props: StoreAdminNavigationScreenProps<"IntegrationAdmin">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <InnerIntegrationsAdminScreen {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerIntegrationsAdminScreen(
  props: StoreAdminNavigationScreenProps<"IntegrationAdmin"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<IntegrationsAdminScreenQuery>(
    graphql`
      query IntegrationsAdminScreenQuery {
        store {
          myRole

          ...IntegrationAdminHRProviderListItem_Store
          ...IntegrationAdminServiceAccountsListItem_Store
          hasSoftOneOrgIntegration
          id
          integrationFlyers {
            __typename
            id
            ...IntegrationAdminFlyerListItem_IntegrationEmaginFlyer
          }
          integrationPollFiles {
            __typename
            id
            ...IntegrationAdminPollFileListItem_IntegrationPollFile
          }
          integrationPushAppy {
            __typename
            id
          }
          integrationTilTid {
            __typename
            id
          }
          integrationTooEasy {
            __typename
            id
          }
          integrationSoftOne {
            __typename
            id
          }
          integrationTurnpike {
            id
            __typename
          }
          integrationQuinyx {
            id
            __typename
          }

          integrationFlourish {
            __typename
            id
          }
          integrationTomra {
            __typename
          }
          integrationInvafresh {
            __typename
          }
          integrationBokaklipp {
            __typename
          }
          integrationNewsfeeds {
            __typename
            ...IntegrationNewsfeedScreen_IntegrationNewsfeedListItem
          }
          integrationWebFetchers {
            __typename
            id
            ...IntegrationAdminWebFetcherListItem_IntegrationWebFetcher
          }
          integrationICAMinButik {
            __typename
            ...IntegrationAdminIcaMinButikListItem_IntegrationWebFetcher
            status
          }
          serviceAccounts {
            __typename
            id
          }
        }
      }
    `,
    {},
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const intl = useIntl();

  const hasICA = useHasFeatureFlag(FeatureFlags.ICAMinButikIntegration);

  const isAdmin = useIsStoresprintAdmin();

  const hasWebFetching = isAdmin || __DEV__;
  const sections = useMemo(() => {
    const hasHR =
      data.store.integrationTooEasy ||
      data.store.integrationTilTid ||
      data.store.integrationSoftOne ||
      data.store.integrationQuinyx;
    return [
      {
        key: "active",
        title: intl.formatMessage({
          description: "StoreAdminActiveIntegrations Title",
          defaultMessage: "Active Integrations",
        }),
        data: [
          ...(data.store.integrationICAMinButik
            ? [data.store.integrationICAMinButik]
            : []),

          ...(data.store.integrationTurnpike
            ? [data.store.integrationTurnpike]
            : []),
          ...(data.store.integrationTooEasy
            ? [data.store.integrationTooEasy]
            : []),
          ...(data.store.integrationTilTid
            ? [data.store.integrationTilTid]
            : []),
          ...(data.store.integrationSoftOne
            ? [data.store.integrationSoftOne]
            : []),
          ...(data.store.integrationQuinyx
            ? [data.store.integrationQuinyx]
            : []),
          ...(data.store.integrationFlourish
            ? [data.store.integrationFlourish]
            : []),
          ...(data.store.integrationPushAppy
            ? [data.store.integrationPushAppy]
            : []),
          ...(data.store.integrationFlyers ?? []),
          ...(data.store.integrationPollFiles ?? []),
          ...(data.store.integrationTomra ? [data.store.integrationTomra] : []),
          ...(data.store.integrationInvafresh
            ? [data.store.integrationInvafresh]
            : []),
          ...(data.store.integrationBokaklipp
            ? [data.store.integrationBokaklipp]
            : []),
          ...(data.store.integrationNewsfeeds ?? []),
          ...(data.store.integrationWebFetchers ?? []),
          ...(data.store.serviceAccounts.length > 0
            ? [{ __typename: "ServiceAccounts" }]
            : []),
        ] as const,
      },
      {
        key: "add",
        title: intl.formatMessage({
          defaultMessage: "Add Integration",
          description: "StoreAdmin Add Integration Title",
        }),
        data: [
          ...(hasICA && !data.store.integrationICAMinButik
            ? [{ __typename: "AddICA" }]
            : []),
          { __typename: "AddEmagin" },
          ...(!data.store.integrationTurnpike
            ? [{ __typename: "AddTurnpike" }]
            : []),
          ...(!data.store.integrationFlourish
            ? [{ __typename: "AddFlourish" }]
            : []),
          ...(!data.store.integrationPushAppy
            ? [{ __typename: "AddPushAppy" }]
            : []),
          ...(!hasHR
            ? [
                { __typename: "AddTilTid" },
                { __typename: "AddSoftOne" },
                { __typename: "AddQuniyx" },
              ]
            : []),
          ...(!data.store.integrationInvafresh
            ? [{ __typename: "AddInvafresh" }]
            : []),
          { __typename: "AddNewsfeed" },
          ...(!data.store.integrationBokaklipp
            ? [{ __typename: "AddBokaklipp" }]
            : []),
          ...(!data.store.integrationTomra && isAdmin
            ? [
                {
                  __typename: "AddTomra",
                },
              ]
            : []),
          { __typename: "AddServiceAccount" },
          ...(hasWebFetching ? [{ __typename: "AddWebFetcher" }] : []),
        ] as const,
      },
    ].filter((v) => v.data.length !== 0);
  }, [
    data.store.integrationTooEasy,
    data.store.integrationTilTid,
    data.store.integrationSoftOne,
    data.store.integrationQuinyx,
    data.store.integrationTurnpike,
    data.store.integrationICAMinButik,
    data.store.integrationFlourish,
    data.store.integrationPushAppy,
    data.store.integrationFlyers,
    data.store.integrationPollFiles,
    data.store.integrationTomra,
    data.store.integrationInvafresh,
    data.store.integrationBokaklipp,
    data.store.integrationNewsfeeds,
    data.store.integrationWebFetchers,
    data.store.serviceAccounts.length,
    intl,
    hasICA,
    isAdmin,
    hasWebFetching,
  ]);

  const contentContainerStyle = useBoxStyle({
    spaceHorizontal: "medium",
    spaceVertical: "large",
  });

  return (
    <SectionList
      contentContainerStyle={contentContainerStyle}
      ItemSeparatorComponent={AHSeparator}
      stickySectionHeadersEnabled={false}
      renderSectionHeader={({ section }) => {
        return (
          <ListSectionHeader>
            <ListSectionHeaderTitle>{section.title}</ListSectionHeaderTitle>
          </ListSectionHeader>
        );
      }}
      renderSectionFooter={() => <Box spaceBottom="large" />}
      sections={sections}
      renderItem={({ item, index, section }) => {
        const sharedProps = {
          isFirst: index === 0,
          isLast: index === section.data.length - 1,
        };
        switch (item.__typename) {
          case "AddICA":
            return (
              <IntegrationTextItem
                {...sharedProps}
                mode="ADD"
                onPress={() => {
                  props.navigation.navigate("IntegrationICAScreen", {});
                }}
              >
                <FormattedMessage
                  description="IntegrationsAdminICA addItem title"
                  defaultMessage="ICA Min Butik"
                />
              </IntegrationTextItem>
            );
          case "AddWebFetcher":
            return (
              <IntegrationTextItem
                {...sharedProps}
                mode="ADD"
                onPress={() => {
                  props.navigation.navigate("IntegrationWebFetcherCreate", {});
                }}
              >
                <FormattedMessage
                  description="IntegrationsAdminWebFetcher addItem title"
                  defaultMessage="Web Fetcher"
                />
              </IntegrationTextItem>
            );

          case "AddServiceAccount":
            return (
              <IntegrationTextItem
                {...sharedProps}
                mode="ADD"
                onPress={() =>
                  props.navigation.navigate("ServiceAccountCreate", {})
                }
              >
                <FormattedMessage
                  description="IntegrationAddItem - service account - title"
                  defaultMessage="External Service"
                />
              </IntegrationTextItem>
            );
          case "AddFlourish":
          case "IntegrationFlourish":
            return (
              <IntegrationLogoItem
                {...sharedProps}
                mode={item.__typename === "AddFlourish" ? "ADD" : "positive"}
                logoType="flourish"
                onPress={() => {
                  props.navigation.navigate("Integration", {
                    type: "flourish",
                    mode: item.__typename === "AddFlourish" ? "create" : "edit",
                  });
                }}
              />
            );

          case "AddTurnpike":
          case "IntegrationTurnpike":
            return (
              <IntegrationLogoItem
                {...sharedProps}
                mode={item.__typename === "AddTurnpike" ? "ADD" : "positive"}
                logoType="turnpike"
                onPress={() => {
                  if (item.__typename === "AddTurnpike") {
                    props.navigation.navigate("IntegrationTurnpikeCreate", {});
                  }
                }}
              />
            );
          case "AddPushAppy":
          case "IntegrationPushAppy":
            return (
              <IntegrationLogoItem
                logoType="pushappy"
                mode={item.__typename === "AddPushAppy" ? "ADD" : "positive"}
                {...sharedProps}
                onPress={() => {
                  props.navigation.navigate("Integration", {
                    type: "pushappy",
                    mode: item.__typename === "AddPushAppy" ? "create" : "edit",
                  });
                }}
              />
            );
          case "AddEmagin":
            return (
              <IntegrationTextItem
                {...sharedProps}
                mode="ADD"
                onPress={() => {
                  props.navigation.navigate("IntegrationFlyerCreate", {});
                }}
              >
                <FormattedMessage
                  description="IntegrationsAdminFlyer addItem title"
                  defaultMessage="Online Leaflet"
                />
              </IntegrationTextItem>
            );

          case "AddTilTid":
            return (
              <IntegrationLogoItem
                logoType="tiltid"
                mode="ADD"
                {...sharedProps}
                onPress={() => {
                  props.navigation.navigate("IntegrationTilTidCreate", {});
                }}
              />
            );
          case "AddSoftOne":
            return (
              <IntegrationLogoItem
                {...sharedProps}
                logoType="softone"
                mode="ADD"
                onPress={() => {
                  if (data.store.hasSoftOneOrgIntegration) {
                    props.navigation.navigate(
                      "IntegrationSoftOneOrgCreate",
                      {}
                    );
                  } else {
                    props.navigation.navigate("IntegrationSoftOneCreate", {});
                  }
                }}
              />
            );
          case "AddQuniyx":
            return (
              <IntegrationLogoItem
                {...sharedProps}
                logoType="quinyx"
                mode="ADD"
                onPress={() => {
                  props.navigation.navigate("IntegrationQuinyxCreate", {});
                }}
              />
            );
          case "IntegrationInvafresh":
          case "AddInvafresh":
            return (
              <IntegrationLogoItem
                {...sharedProps}
                logoType="upshop"
                mode={item.__typename === "AddInvafresh" ? "ADD" : "positive"}
                onPress={() => {
                  if (item.__typename === "AddInvafresh") {
                    props.navigation.navigate("IntegrationInvafreshCreate", {});
                  }
                }}
              />
            );
          case "AddNewsfeed":
            return (
              <IntegrationTextItem
                {...sharedProps}
                mode="ADD"
                onPress={() => {
                  props.navigation.navigate("IntegrationNewsfeedCreate", {});
                }}
              >
                <FormattedMessage
                  description="IntegrationsAdminNewsfeed addItem title"
                  defaultMessage="Newsfeed"
                />
              </IntegrationTextItem>
            );
          case "IntegrationBokaklipp":
          case "AddBokaklipp":
            return (
              <IntegrationLogoItem
                {...sharedProps}
                mode={item.__typename === "AddBokaklipp" ? "ADD" : "positive"}
                logoType="bokaklipp"
                onPress={() => {
                  props.navigation.navigate("Integration", {
                    type: "bokaklipp",
                    mode:
                      item.__typename === "AddBokaklipp" ? "create" : "edit",
                  });
                }}
              />
            );
          case "AddTomra":
          case "IntegrationTomra":
            return (
              <IntegrationLogoItem
                {...sharedProps}
                mode={item.__typename === "AddTomra" ? "ADD" : "positive"}
                logoType="tomra"
                onPress={() => {
                  if (item.__typename === "AddTomra") {
                    props.navigation.navigate("IntegrationTomraCreate", {});
                  } else {
                    props.navigation.navigate("IntegrationTomra", {});
                  }
                }}
              />
            );

          case "IntegrationEmaginFlyer":
            return (
              <IntegrationAdminFlyerListItem
                {...sharedProps}
                flyer={item as any}
              />
            );
          case "IntegrationPollFile":
            return (
              <IntegrationAdminPollFileListItem
                {...sharedProps}
                pollFile={item as any}
              />
            );

          case "IntegrationTooEasy":
          case "IntegrationQuinyx":
          case "IntegrationSoftOne":
          case "IntegrationTilTid": {
            const icon = {
              IntegrationQuinyx: "quinyx",
              IntegrationSoftOne: "softone",
              IntegrationTilTid: "tiltid",
              IntegrationTooEasy: "tooeasy",
            } as const;
            return (
              <IntegrationAdminHRProviderListItem
                {...sharedProps}
                icon={icon[item.__typename]}
                onPress={() => {
                  props.navigation.navigate("EmployeeScheduleEdit", {});
                }}
                disabled={data.store.myRole !== "ADMIN"}
                store={data.store}
              />
            );
          }

          case "IntegrationNewsfeed":
            return (
              <IntegrationAdminNewsfeedListItem
                newsfeed={item as any}
                {...sharedProps}
                navigation={props.navigation}
              />
            );

          case "ServiceAccounts":
            return (
              <IntegrationAdminServiceAccountsListItem
                {...sharedProps}
                onPress={() => props.navigation.navigate("ServiceAccounts", {})}
                store={data.store}
              />
            );
          case "IntegrationWebFetch":
            return (
              <IntegrationAdminWebFetcherListItem
                {...sharedProps}
                integration={item as any}
              />
            );
          case "ICAMinButikIntegration":
            return (
              <IntegrationAdminIcaMinButikListItemListItem
                {...sharedProps}
                integration={item as any}
                onPress={() => {
                  props.navigation.navigate("IntegrationICAScreen", {});
                }}
              />
            );
          default:
            return null;
        }
      }}
    />
  );
}
